import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function SeoHead() {
  useEffect(() => {
    // Organization schema
    const orgSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'PPP Exports',
      url: SITE_URL,
      logo: `${SITE_URL}/favicon-96x96.png`,
      description:
        'Exportadores costarricenses de palmito, piña tropical y alimentos procesados premium con más de 30 años de experiencia en el sector agroindustrial.',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'San José',
        addressRegion: 'SJ',
        addressCountry: 'CR',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 9.9281,
        longitude: -84.0907,
      },
      areaServed: [
        { '@type': 'Continent', name: 'South America' },
        { '@type': 'Continent', name: 'North America' },
        { '@type': 'Continent', name: 'Europe' },
        { '@type': 'Continent', name: 'Asia' },
      ],
      sameAs: [],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'sales',
        availableLanguage: ['Spanish', 'English'],
      },
    };

    // WebSite schema
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'PPP Exports',
      url: SITE_URL,
      description:
        'Exportadores costarricenses de palmito, piña tropical y alimentos procesados premium.',
      inLanguage: ['es', 'en'],
    };

    // WebPage schema
    const webpageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'PPP Exports | Exportación Palmito & Piña Costa Rica | San José',
      url: SITE_URL,
      description:
        'PPP Exports: exportadores costarricenses de palmito, piña tropical y alimentos procesados premium desde San José. +30 años de experiencia, estándares internacionales, presencia en 4 continentes.',
      isPartOf: { '@id': SITE_URL },
      about: { '@type': 'Organization', name: 'PPP Exports' },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Inicio',
            item: SITE_URL,
          },
        ],
      },
    };

    // Product catalog schema
    const productCatalogSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Portafolio de Exportación PPP Exports',
      description: 'Productos agrícolas procesados para exportación: palmito y piña costarricense.',
      numberOfItems: 9,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Product',
            name: 'Palmito Triturado',
            description: 'Palmito costarricense triturado, listo para exportación con estándares internacionales.',
            brand: { '@type': 'Brand', name: 'PPP Exports' },
            countryOfOrigin: 'CR',
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Product',
            name: 'Palmito Entero',
            description: 'Palmito costarricense entero envasado, certificado para exportación.',
            brand: { '@type': 'Brand', name: 'PPP Exports' },
            countryOfOrigin: 'CR',
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Product',
            name: 'Palmito en Trozos',
            description: 'Palmito costarricense en trozos, ideal para mercados internacionales.',
            brand: { '@type': 'Brand', name: 'PPP Exports' },
            countryOfOrigin: 'CR',
          },
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Product',
            name: 'Piña Triturada',
            description: 'Piña tropical costarricense triturada, procesada con estándares internacionales.',
            brand: { '@type': 'Brand', name: 'PPP Exports' },
            countryOfOrigin: 'CR',
          },
        },
        {
          '@type': 'ListItem',
          position: 5,
          item: {
            '@type': 'Product',
            name: 'Piña en Trozos',
            description: 'Piña tropical costarricense en trozos para exportación global.',
            brand: { '@type': 'Brand', name: 'PPP Exports' },
            countryOfOrigin: 'CR',
          },
        },
        {
          '@type': 'ListItem',
          position: 6,
          item: {
            '@type': 'Product',
            name: 'Piña en Rodajas',
            description: 'Piña tropical costarricense en rodajas, lista para distribución internacional.',
            brand: { '@type': 'Brand', name: 'PPP Exports' },
            countryOfOrigin: 'CR',
          },
        },
      ],
    };

    // FAQ schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '¿Qué productos exporta PPP Exports?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'PPP Exports exporta palmito costarricense en múltiples presentaciones (triturado, entero, en trozos, en lasaña, arroz, pasta cabello de ángel) y piña tropical (triturada, en trozos, en rodajas). Todos los productos cuentan con certificación orgánica.',
          },
        },
        {
          '@type': 'Question',
          name: '¿A qué países exporta PPP Exports?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'PPP Exports tiene presencia consolidada en Sudamérica, Norteamérica, Europa y Asia. Nuestros productos están disponibles en cadenas como Walmart, HEB, Whole Foods y Publix en Estados Unidos.',
          },
        },
        {
          '@type': 'Question',
          name: '¿Cuántos años de experiencia tiene PPP Exports en exportación?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'PPP Exports cuenta con más de 30 años de experiencia en el sector agroindustrial costarricense, trabajando directamente con familias productoras locales y cumpliendo estándares internacionales de calidad e inocuidad.',
          },
        },
        {
          '@type': 'Question',
          name: '¿Cómo solicitar una cotización a PPP Exports?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Puede solicitar una cotización personalizada completando el formulario de contacto en nuestro sitio web. Ofrecemos precios adaptados según volumen, presentación, empaque y destino.',
          },
        },
      ],
    };

    const schemas = [orgSchema, websiteSchema, webpageSchema, productCatalogSchema, faqSchema];

    const existingScripts = document.querySelectorAll('script[data-schema="ppp-exports"]');
    existingScripts.forEach((s) => s.remove());

    schemas.forEach((schema, idx) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'ppp-exports');
      script.setAttribute('data-schema-idx', String(idx));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="ppp-exports"]').forEach((s) => s.remove());
    };
  }, []);

  return null;
}
